/**
 * Webmail — Iter Feb 2026 R47 · Phase 3.
 * Gmail-style 3-column layout:
 *   [ Mailbox switcher + Folder tree ]  [ Message list ]  [ Message viewer ]
 *
 * Multi-mailbox: shows all workspace-shared + my personal mailboxes.
 * Real-time push: subscribes to the workspace WS channel and refreshes on
 * `mail:new` events emitted by the IMAP reply poller.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Mail, Inbox, Send, FileText, AlertTriangle, Trash2, Folder,
  RefreshCw, PenLine, Loader2, Star, Archive, ArrowLeft, X, Search,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import MailComposer from "@/components/mail/MailComposer";
import MailboxEditor from "@/components/mail/MailboxEditor";

const FOLDER_ICON = {
  inbox: Inbox, sent: Send, drafts: FileText, spam: AlertTriangle, trash: Trash2, other: Folder,
};

const relTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return d.toLocaleDateString();
};

export default function Webmail() {
  const { activeWorkspaceId, user } = useAuth();
  const [mailboxes, setMailboxes] = useState([]);
  const [activeMbId, setActiveMbId] = useState(null);
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState("INBOX");
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState(false);
  const [search, setSearch] = useState("");
  const [composeOpen, setComposeOpen] = useState(false);
  const [editorMbId, setEditorMbId] = useState(null);
  const [showEditor, setShowEditor] = useState(false);

  const activeMb = useMemo(() => mailboxes.find(m => m.id === activeMbId) || null, [mailboxes, activeMbId]);

  const loadMailboxes = async () => {
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/mailboxes`);
      setMailboxes(data.rows || []);
      if (data.rows?.length && !activeMbId) setActiveMbId(data.rows[0].id);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load mailboxes")); }
  };
  useEffect(() => { if (activeWorkspaceId) loadMailboxes(); }, [activeWorkspaceId]); // eslint-disable-line

  const loadFolders = async () => {
    if (!activeMbId) return;
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/mailboxes/${activeMbId}/folders`);
      setFolders(data.rows || []);
      const inb = (data.rows || []).find(f => f.kind === "inbox") || data.rows?.[0];
      if (inb) setActiveFolder(inb.name);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load folders")); setFolders([]); }
  };
  useEffect(() => { if (activeMbId) loadFolders(); }, [activeMbId]); // eslint-disable-line

  const loadList = async () => {
    if (!activeMbId || !activeFolder) return;
    setLoadingList(true); setSelected(null);
    try {
      const { data } = await api.get(
        `/workspaces/${activeWorkspaceId}/mailboxes/${activeMbId}/messages`,
        { params: { folder: activeFolder, limit: 50, search: search || undefined } },
      );
      setMessages(data.rows || []); setTotal(data.total || 0);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load messages")); }
    finally { setLoadingList(false); }
  };
  useEffect(() => { loadList(); }, [activeMbId, activeFolder]); // eslint-disable-line

  const openMessage = async (m) => {
    setLoadingMsg(true);
    try {
      const { data } = await api.get(
        `/workspaces/${activeWorkspaceId}/mailboxes/${activeMbId}/messages/${encodeURIComponent(activeFolder)}/${m.uid}`);
      setSelected(data);
      // Optimistically mark seen locally so list badge updates
      setMessages(prev => prev.map(x => x.uid === m.uid ? { ...x, seen: true } : x));
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to open message")); }
    finally { setLoadingMsg(false); }
  };

  const moveTo = async (destFolder) => {
    if (!selected) return;
    try {
      await api.post(
        `/workspaces/${activeWorkspaceId}/mailboxes/${activeMbId}/messages/${encodeURIComponent(activeFolder)}/${selected.uid}/move`,
        { dest_folder: destFolder },
      );
      toast.success(`Moved to ${destFolder}`);
      setSelected(null); loadList();
    } catch (e) { toast.error(apiErrorMessage(e, "Move failed")); }
  };

  const del = async () => {
    if (!selected) return;
    if (!confirm("Delete this message?")) return;
    try {
      await api.delete(
        `/workspaces/${activeWorkspaceId}/mailboxes/${activeMbId}/messages/${encodeURIComponent(activeFolder)}/${selected.uid}`);
      toast.success("Deleted");
      setSelected(null); loadList();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  // R47 · Real-time — refresh on `mail:new` WS events. Uses the app's
  // existing workspace WS channel; we just subscribe to the event type.
  useEffect(() => {
    if (!activeWorkspaceId) return;
    const handler = (e) => {
      const t = e.detail?.type;
      if (t === "mail:new") { toast.info(`${e.detail.count} new email${e.detail.count === 1 ? "" : "s"}`); loadList(); }
    };
    window.addEventListener("ws-event", handler);
    return () => window.removeEventListener("ws-event", handler);
  }, [activeMbId, activeFolder]); // eslint-disable-line

  const trashFolder = folders.find(f => f.kind === "trash")?.name;
  const spamFolder = folders.find(f => f.kind === "spam")?.name;
  const draftsFolder = folders.find(f => f.kind === "drafts")?.name || "Drafts";

  return (
    <div className="space-y-3" data-testid="webmail-page">
      <PageHeader
        eyebrow="Communications"
        title="Webmail"
        subtitle="Send, receive and manage your BigRock / HostGator / Zoho / Gmail inboxes right inside Acquilix."
        right={
          <div className="flex items-center gap-2 flex-wrap">
            {mailboxes.length > 0 && (
              <select
                value={activeMbId || ""}
                onChange={e => setActiveMbId(e.target.value)}
                className="h-9 border border-border px-2 text-sm bg-background rounded-none min-w-[220px]"
                data-testid="mailbox-switcher"
              >
                {mailboxes.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.name}{m.owner_user_id ? " · personal" : " · shared"}
                  </option>
                ))}
              </select>
            )}
            <Button size="sm" variant="outline" onClick={() => { setEditorMbId(null); setShowEditor(true); }} className="rounded-none border-purple-500 text-purple-700 hover:bg-purple-50" data-testid="add-mailbox">
              + Add mailbox {mailboxes.length > 0 && `(${mailboxes.length})`}
            </Button>
            <Button size="sm" onClick={() => setComposeOpen(true)} className="rounded-none bg-purple-600 hover:bg-purple-700 text-white" data-testid="new-mail">
              <PenLine className="w-3.5 h-3.5 mr-1" /> Compose new
            </Button>
          </div>
        }
      />

      {mailboxes.length === 0 ? (
        <div className="border border-dashed border-border p-8 text-center text-sm">
          <Mail className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
          <div className="font-semibold mb-1">No mailbox connected yet</div>
          <p className="text-muted-foreground max-w-md mx-auto mb-4">
            Add your first mailbox to send + receive email inside Acquilix.
            Works with BigRock, HostGator, cPanel, Zoho, Gmail (app-password), or any IMAP+SMTP server.
          </p>
          <Button onClick={() => { setEditorMbId(null); setShowEditor(true); }} data-testid="add-first-mailbox">
            + Add mailbox
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-3 border border-border" style={{ minHeight: "calc(100vh - 220px)" }}>
          {/* Folder column */}
          <div className="col-span-2 border-r border-border p-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Folders</div>
            {folders.map(f => {
              const Icon = FOLDER_ICON[f.kind] || Folder;
              const active = f.name === activeFolder;
              return (
                <button
                  key={f.name}
                  onClick={() => setActiveFolder(f.name)}
                  className={`w-full text-left px-2 py-1 text-xs flex items-center gap-1.5 ${active ? "bg-neutral-900 text-white" : "hover:bg-neutral-100"}`}
                  data-testid={`folder-${f.kind || "other"}`}
                >
                  <Icon className="w-3 h-3 shrink-0" />
                  <span className="truncate">{f.name}</span>
                </button>
              );
            })}
            <div className="mt-4">
              <Button size="sm" variant="outline" onClick={loadFolders} className="rounded-none h-7 text-[11px] w-full">
                <RefreshCw className="w-3 h-3 mr-1" /> Refresh
              </Button>
              {activeMb && (
                <Button size="sm" variant="ghost" onClick={() => { setEditorMbId(activeMbId); setShowEditor(true); }} className="rounded-none h-7 text-[11px] w-full mt-1">
                  Edit mailbox
                </Button>
              )}
            </div>
          </div>

          {/* Message list */}
          <div className="col-span-4 border-r border-border flex flex-col">
            <div className="p-2 border-b border-border flex items-center gap-1">
              <Search className="w-3 h-3 text-muted-foreground" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") loadList(); }}
                placeholder="Search in this folder"
                className="h-7 rounded-none text-xs"
                data-testid="mail-search"
              />
              <Button size="sm" variant="ghost" onClick={loadList} className="rounded-none h-7">
                <RefreshCw className="w-3 h-3" />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loadingList ? (
                <div className="text-xs text-muted-foreground py-8 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>
              ) : messages.length === 0 ? (
                <div className="text-xs text-muted-foreground py-8 text-center">Nothing in this folder</div>
              ) : (
                messages.map(m => (
                  <button
                    key={m.uid}
                    onClick={() => openMessage(m)}
                    className={`w-full text-left border-b border-border px-2 py-1.5 hover:bg-neutral-50 ${selected?.uid === m.uid ? "bg-sky-50 border-l-2 border-l-sky-500" : ""} ${!m.seen ? "font-semibold" : ""}`}
                    data-testid={`mail-item-${m.uid}`}
                  >
                    <div className="flex items-center gap-1 text-xs">
                      <span className="truncate flex-1">{m.from || "(no sender)"}</span>
                      <span className="text-[10px] text-muted-foreground whitespace-nowrap">{relTime(m.date)}</span>
                    </div>
                    <div className="text-xs truncate">{m.subject || "(no subject)"}</div>
                    <div className="flex items-center gap-1 mt-0.5 text-[9px] text-muted-foreground">
                      {m.flagged && <Star className="w-2.5 h-2.5 text-amber-500 fill-amber-400" />}
                      {m.answered && <span>↩</span>}
                    </div>
                  </button>
                ))
              )}
            </div>
            {total > messages.length && (
              <div className="text-[10px] text-muted-foreground text-center py-1 border-t">
                Showing {messages.length} of {total}
              </div>
            )}
          </div>

          {/* Message viewer */}
          <div className="col-span-6 p-3 overflow-y-auto">
            {loadingMsg ? (
              <div className="text-xs text-muted-foreground py-8 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>
            ) : !selected ? (
              <div className="text-xs text-muted-foreground py-16 text-center">
                <Mail className="w-8 h-8 mx-auto mb-2 opacity-30" />
                Select a message to preview
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-1 mb-2 border-b pb-2">
                  <Button size="sm" variant="ghost" onClick={() => setSelected(null)} className="rounded-none h-7"><ArrowLeft className="w-3 h-3" /></Button>
                  {spamFolder && activeFolder !== spamFolder && (
                    <Button size="sm" variant="outline" onClick={() => moveTo(spamFolder)} className="rounded-none h-7 text-[11px]" data-testid="mail-move-spam">
                      <AlertTriangle className="w-3 h-3 mr-1" /> Spam
                    </Button>
                  )}
                  {trashFolder && activeFolder !== trashFolder && (
                    <Button size="sm" variant="outline" onClick={() => moveTo(trashFolder)} className="rounded-none h-7 text-[11px]" data-testid="mail-move-trash">
                      <Archive className="w-3 h-3 mr-1" /> Move to Trash
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={del} className="rounded-none h-7 text-[11px] text-rose-600" data-testid="mail-delete">
                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                  </Button>
                  <div className="flex-1" />
                  <Button size="sm" onClick={() => setComposeOpen(true)} className="rounded-none h-7 text-[11px]" data-testid="mail-reply">
                    Reply
                  </Button>
                </div>
                <h2 className="text-base font-semibold">{selected.subject || "(no subject)"}</h2>
                <div className="text-[11px] text-muted-foreground mb-3">
                  <div>From: <span className="text-foreground">{selected.from}</span></div>
                  <div>To: <span className="text-foreground">{selected.to}</span></div>
                  {selected.cc && <div>Cc: <span className="text-foreground">{selected.cc}</span></div>}
                  {selected.date && <div>{new Date(selected.date).toLocaleString()}</div>}
                </div>
                {selected.attachments?.length > 0 && (
                  <div className="border p-2 mb-3">
                    <div className="text-[10px] uppercase text-muted-foreground mb-1">Attachments ({selected.attachments.length})</div>
                    {selected.attachments.map((a, i) => (
                      <div key={i} className="text-xs">{a.filename} ({Math.round((a.size||0)/1024)} KB)</div>
                    ))}
                  </div>
                )}
                {selected.html ? (
                  <div className="border-t pt-3 max-w-full overflow-x-auto" dangerouslySetInnerHTML={{ __html: selected.html }} />
                ) : (
                  <pre className="border-t pt-3 text-xs whitespace-pre-wrap font-sans">{selected.text}</pre>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {composeOpen && (
        <MailComposer
          open={composeOpen}
          onOpenChange={setComposeOpen}
          entityType="contact"
          entityId="webmail"
          defaultTo={selected?.from?.match(/<(.+)>/)?.[1] || ""}
          defaultSubject={selected ? (selected.subject?.startsWith("Re:") ? selected.subject : `Re: ${selected.subject}`) : ""}
          defaultMailboxId={activeMbId}
          onSent={() => { setComposeOpen(false); loadList(); }}
        />
      )}
      {showEditor && (
        <MailboxEditor
          open={showEditor}
          onOpenChange={setShowEditor}
          mailboxId={editorMbId}
          onSaved={() => { setShowEditor(false); loadMailboxes(); }}
        />
      )}
    </div>
  );
}
